import type {VoidFunctionComponent} from "react";
import React from "react";
import s from "./ButtonDocumentation.module.scss";
import Button from "../../../components/common/Button";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import {
  SectionAwardIcon
} from "../../../components/lesson/sectionEndModal/sectionEndAward/sectionAwardIcon/SectionAwardIcon";
import {getLoremIpsumText} from "../../../util/string-util";

export interface IButtonDocumentationProps {
}

export const ButtonDocumentation: VoidFunctionComponent<IButtonDocumentationProps> = ({}) => {
  let propertyIndex = 0;

  const properties: IPropertyWrapper[] = [
    {
      isRequired: true,
      name: "text",
      element: <Property
        key={++propertyIndex}
        name={"text"}
        description={"The label for the button."}
        isRequired={true}
        type={"string | ReactNode"}
        examples={<>
          <Example title={"text = 'My button'"}>
            <Button
              onClick={() => {}}
              text={"My button"}
            />
          </Example>
          <br />
          <Example title={"text is a ReactNode"}>
            <Button
              onClick={() => {}}
              text={<>
                <img src={"favicon.png"}/>
              </>}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "buttonType",
      element: <Property
        key={++propertyIndex}
        name={"buttonType"}
        description={'Determines the visual appearance of the button. "primary" buttons are white with purple border and text. "secondary" buttons are purple with white border and text.'}
        isRequired={false}
        type={'"primary" | "secondary"'}
        defaultValue={'"primary"'}
        examples={<>
          <Example title={'buttonType = "primary"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              buttonType={"primary"}
            />
          </Example>
          <br />

          <Example title={'buttonType = "secondary"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              buttonType={"secondary"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "isLoading",
      element: <Property
        key={++propertyIndex}
        name={"isLoading"}
        description={<>
          Determines whether or not to show a spinning icon instead of the normal button label.
          <span className={s.warning}> NOTE: isLoading does not disable the onClick handler.</span>
        </>}
        isRequired={false}
        type={'boolean'}
        defaultValue={'false'}
        examples={<>
          <Example title={'isLoading = "true"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              buttonType={"primary"}
              isLoading={true}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "navigationNote",
      element: <Property
        key={++propertyIndex}
        name={"navigationNote"}
        description={<>
          A midi keyboard shortcut for the button.
          <span className={s.warning}> NOTE: Only works if a keyboard is plugged in. Piano note icon only appears if keyboard is plugged in.</span>
        </>}
        isRequired={false}
        type={'"C" | "D" | "B"'}
        examples={<>
          <Example title={'navigationNote = "D"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              navigationNote={"D"}
              minWidth={200}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "minWidth",
      element: <Property
        key={++propertyIndex}
        name={"minWidth"}
        description={'Determines the minimize width of a button. Measured in pixels.'}
        isRequired={false}
        type={'number'}
        examples={<>
          <Example title={'minWidth = 400 pixels'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              minWidth={400}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "disabled",
      element: <Property
        key={++propertyIndex}
        name={"disabled"}
        description={<>
          Enables / disables the onClick handler.
          <span className={s.warning}> NOTE: button appearance / UI effects are not affected.</span>
        </>}
        isRequired={false}
        type={'boolean'}
        examples={<>
          <Example title={'disabled = "true"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              disabled={true}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "icon",
      element: <Property
        key={++propertyIndex}
        name={"icon"}
        description={<>
          Causes an icon to appear on the button to the left of the text.
        </>}
        isRequired={false}
        type={'ReactElement'}
        examples={<>
          <Example title={'navigationNote and icon set"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              icon={<div><SectionAwardIcon sectionNumber={4} /></div>}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "ariaLabel",
      element: <Property
        key={++propertyIndex}
        name={"ariaLabel"}
        description={<>
          Causes an aria label to be associated with the inner browser button.
        </>}
        isRequired={false}
        type={'string'}
        examples={<>
          <Example title={'ariaLabel set"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              ariaLabel={"Super useful aria label"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "additionalClassName",
      element: <Property
        key={++propertyIndex}
        name={"additionalClassName"}
        description={<>
          Allows passing in additional css class names to the button for custom styling.
        </>}
        isRequired={false}
        type={'space separated css class(es)'}
        examples={<>
          <Example title={'additionalClassName set"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
              additionalClassName={"my-custom-class another-class"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: true,
      name: "onClick",
      element: <Property
        key={++propertyIndex}
        name={"onClick"}
        description={<>
          A javascript function that is called when the button is clicked.
        </>}
        isRequired={true}
        type={'() => void'}
        examples={<>
          <Example title={'additionalClassName set"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "children",
      element: <Property
        key={++propertyIndex}
        name={"children"}
        description={<>
          An additional React node that should appear inside of the button after the text label.
        </>}
        isRequired={false}
        type={'React.ReactNode'}
        examples={<>
          <Example title={'additionalClassName set"'}>
            <Button
              onClick={() => {}}
              text={"Click me"}
            >
              <>
                {getLoremIpsumText(100)}
              </>
            </Button>
          </Example>
        </>}
      />
    },
  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"Button"}
      description={"A button performs an action when clicked."}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}
  </div>;
}
